import React from 'react';
import { Field, Form } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { required } from 'common/forms/FormValidationHelper';
import { useSelector } from 'hooks/useSelector';
import { forgotPassword } from 'modules/auth/actions';
import { getForgotPasswordIsLoading } from 'modules/auth/selectors';
import { getCustomName } from 'modules/users/selectors/currentUser';
import { Timeline, Card, Button, Input, Form as UIForm } from 'ui/base';
import { b } from 'utils/i18nUtils';

import { cardStyle } from './UI';

const TimelineContent = ({ handleSubmit, hasValidationErrors }) => {
  const intl = useIntl();
  const loading = useSelector(getForgotPasswordIsLoading);

  return (
    <>
      <FormattedMessage
        tagName="strong"
        defaultMessage="I know my username"
        id="page.forgotPassword.knowMyEmailAddress.title"
      />
      <FormattedMessage
        tagName="div"
        id="page.forgotPassword.knowMyEmailAddress.message"
        defaultMessage="Please enter your <strong>username</strong> below. Please note that it may be different from the email address you currently use."
        values={{ strong: b }}
      />
      <Field name="username" validate={required} subscription={{ value: true }}>
        {({ input }) => (
          <UIForm.Item className="m-0">
            <Input
              {...input}
              disabled={loading}
              placeholder={intl.formatMessage({ id: 'username', defaultMessage: 'Username' })}
              prefix={<span className="fb-icon--management-user" />}
              data-cy="username-input"
            />
            <Button
              className="mt-2"
              htmlType="submit"
              loading={loading}
              onClick={handleSubmit}
              data-cy="send-reset-email"
              disabled={hasValidationErrors}
            >
              <FormattedMessage id="sendResetEmail" defaultMessage="Send a reset email" />
            </Button>
          </UIForm.Item>
        )}
      </Field>
    </>
  );
};

const ForgotFormPassword = ({ handleSubmit, hasValidationErrors }) => {
  const navigate = useNavigate();
  const loading = useSelector(getForgotPasswordIsLoading);

  const handleOnClick = React.useCallback(() => navigate('/'), [navigate]);

  const timelineItems = React.useMemo(() => [{
    dot: <span className="fb-icon--mail" />,
    children: <TimelineContent handleSubmit={handleSubmit} hasValidationErrors={hasValidationErrors} />
  }, {
    dot: <span className="fb-icon--chat-messages-bubble" />,
    children: (
      <>
        <FormattedMessage
          tagName="strong"
          defaultMessage="I don't know my username"
          id="page.forgotPassword.dontKnowMyEmailAddress.title"
        />
        <FormattedMessage
          tagName="div"
          id="page.forgotPassword.dontKnowMyEmailAddress.message"
          defaultMessage="Please make a request to one of your dealership managers to reset your password."
        />
      </>
    )
  }], [handleSubmit, hasValidationErrors]);

  return (
    <Card
      variant="borderless"
      className={cardStyle}
      title={<FormattedMessage id="resetYourPassword" defaultMessage="Reset your password" />}
    >
      <Timeline items={timelineItems} />
      <div className="border-top mt-4 p-4 text-right">
        <Button onClick={handleOnClick} disabled={loading}>
          <FormattedMessage id="back" defaultMessage="Back" />
        </Button>
      </div>
    </Card>
  );
};

const ForgotPassword = () => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();

  const customName = useSelector(getCustomName);
  const initialValues = { username: location.state?.username };

  const resetPassword = data => {
    dispatch(forgotPassword(data?.username?.trim().toLowerCase()));
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            {
              id: 'app_tab.forgot_password',
              defaultMessage: '{name} · Forgot password',
            },
            { name: customName }
          )}
        </title>
      </Helmet>
      <Form onSubmit={resetPassword} component={ForgotFormPassword} initialValues={initialValues} />
    </>
  );
};

export default ForgotPassword;
